import React, {Component} from 'react';
import {
  Button,
  DatePicker,
  Drawer,
  Empty,
  Icon,
  Input,
  Modal,
  Radio,
  Select,
  Spin,
  Table,
  message
} from 'antd';
import moment from 'moment';
import {Redirect} from 'react-router-dom';
import utils from '../../../utils/common';
import './activationList.less';

const {Option} = Select;
const DATE_FORMAT = 'YYYY-MM-DD';
const PAGE_SIZE = 10;
const BATCH_RENEW_SIZE = 20;
const RENEWAL_LIST_SERVICE = 'get.plugin.renewal.list';
const DEVICE_BIND_SERVICE = 'device.bind';
const ACTIVATION_HISTORY_SERVICE = 'get.activation.history';
const MERCHANT_OPTIONS_SERVICE = 'eps.getMerchants';
const MERCHANT_SEARCH_DEBOUNCE = 300;

const RENEW_OPTIONS = [
  {
    key: '1', period: '365', label: '1年', days: '365天'
  },
  {
    key: '3', period: '1095', label: '3年', days: '1095天'
  },
  {
    key: 'long', period: '9999', label: '长期', days: '9999天'
  }
];

const RENEWAL_VALIDITY_SERVICE = 'device.query.version.validity.period';

const EXPIRY_RANGES = [
  {key: 'all', label: '全部时间'},
  {key: '7', label: '7天内到期', days: 7},
  {key: '15', label: '15天内到期', days: 15},
  {key: '30', label: '30天内到期', days: 30},
  {key: '60', label: '2个月内到期', days: 60},
  {key: '90', label: '3个月内到期', days: 90}
];

const FILTER_INITIAL = {
  agentKeyword: '',
  merchantKeyword: '',
  storeKeyword: '',
  activationCode: '',
  activationStart: null,
  activationEnd: null,
  expiryStart: null,
  expiryEnd: null,
  expiryStatus: '',
  expiryRange: 'all'
};

function formatDate(value) {
  return value ? value.format(DATE_FORMAT) : '';
}

function getLoginValue(...keys) {
  for (let index = 0; index < keys.length; index += 1) {
    const value = window.localStorage.getItem(keys[index]);
    if (value) return value;
  }
  return '';
}

function getExpiryStatusParam(status) {
  if (status === 'expired' || status === '1') return '1';
  if (status === 'unexpired' || status === '2') return '2';
  return '';
}

function formatValidity(days) {
  if (!days) return '';
  const value = String(days);
  if (value === '365') return '1年/365天';
  if (value === '1095') return '3年/1095天';
  if (value === '9999') return '长期/9999天';
  return `${value}天`;
}

function combineCodeAndName(code, name) {
  return [code, name].filter(Boolean).join('/');
}

function splitCodeAndName(value) {
  const text = typeof value === 'string' ? value : '';
  const separatorIndex = text.indexOf('/');
  if (separatorIndex < 0) return {code: text, name: ''};
  return {
    code: text.slice(0, separatorIndex),
    name: text.slice(separatorIndex + 2)
  };
}

function cleanRequestParams(params) {
  if (Array.isArray(params)) {
    return params
      .map(item => cleanRequestParams(item))
      .filter(item => item !== null && item !== undefined && item !== '');
  }
  if (!params || Object.prototype.toString.call(params) !== '[object Object]') return params;
  const cleaned = utils.dealElement(params);
  Object.keys(cleaned).forEach(key => {
    cleaned[key] = cleanRequestParams(cleaned[key]);
  });
  return cleaned;
}

function parseDeviceSnData(value) {
  if (typeof value === 'string') {
    try {
      return parseDeviceSnData(JSON.parse(value));
    } catch (error) {
      return [];
    }
  }
  if (Array.isArray(value)) return value;
  if (value && value.device_sn_data !== undefined) return parseDeviceSnData(value.device_sn_data);
  if (value && value.data !== undefined) return parseDeviceSnData(value.data);
  if (value && Object.prototype.toString.call(value) === '[object Object]') {
    return Object.keys(value).map(key => ({device_sn_expire_date: key, num: value[key]}));
  }
  return [];
}

function getRenewalValidityCounts(response) {
  const payload = response && (response.device_sn_data || response.data || response.body || response);
  const rows = parseDeviceSnData(payload);
  return rows.reduce((counts, item) => {
    const periodValue = item.device_sn_expire_date || item.expire_date || item.period || '';
    const period = String(periodValue).replace(/[^0-9]/g, '');
    const count = Number(item.num || item.count || item.available_num);
    if (period && !Number.isNaN(count)) counts[period] = (counts[period] || 0) + count;
    return counts;
  }, {});
}

function normalizeMerchantOptions(response, type) {
  const list = response && (response.merchantList || response.list || response.rows || response.data);
  if (!Array.isArray(list)) return [];
  const isMerchant = type === 'merchant';
  return list.map(item => {
    const code = isMerchant ? item.merchantCode : item.storeNo;
    const name = isMerchant ? item.nameCn : item.storeFullNameCn;
    return {
      value: `${code || ''}/${name || ''}`,
      label: [code, name].filter(Boolean).join('/')
    };
  }).filter(item => item.value !== '/');
}

function normalizeRenewalRow(item) {
  return {
    ...item,
    id: item.id || item.device_sn,
    agent: combineCodeAndName(item.agent_no, item.agent_name),
    merchant: combineCodeAndName(item.bind_core_merchant_code, item.bind_core_merchant_name),
    store: combineCodeAndName(item.bind_merchant_code, item.bind_merchant_name),
    version: item.goods_name || item.version || '',
    code: item.device_sn || '',
    activationStatus: item.bind_status === '1' ? '已激活' : '未激活',
    expiryStatus: item.expire_status === '1' ? '已过期' : '未过期',
    validity: formatValidity(item.valid_days),
    activationTime: item.bind_date || '',
    expiryTime: item.valid_date || ''
  };
}

function normalizeActivationHistory(response, page, pageSize) {
  const result = response || {};
  const rows = Array.isArray(result.activation_history_list) ? result.activation_history_list : [];
  return {
    list: rows.map((item, index) => ({
      ...item,
      historyKey: `${item.activation_code || 'history'}-${item.activate_time || ''}-${index}`,
      index: (page - 1) * pageSize + index + 1,
      activationCode: item.activation_code || '',
      activationTime: item.activate_time || '',
      expiryTime: item.expire_time || ''
    })),
    total: Number(result.total_result || 0)
  };
}

function normalizeResponse(response) {
  const result = response || {};
  const rows = result.plugin_renewal_list || result.list || result.rows || result.data || [];
  const list = Array.isArray(rows) ? rows : [];
  return {
    list: list.map(normalizeRenewalRow),
    total: Number(result.total_result || result.total || result.totalCount || 0)
  };
}

function getStatusClass(status) {
  if (status === '已激活' || status === '未过期') return 'status-text status-text--success';
  if (status === '已过期' || status === '已到期') return 'status-text status-text--danger';
  return 'status-text';
}

class ActivationList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      filters: {...FILTER_INITIAL},
      list: [],
      total: 0,
      page: 1,
      pageSize: PAGE_SIZE,
      selectedRowKeys: [],
      loading: false,
      merchantOptions: [],
      storeOptions: [],
      merchantOptionsLoading: false,
      storeOptionsLoading: false,
      merchantSearchContent: '',
      storeSearchContent: '',
      redirect: '',
      detail: null,
      detailHistory: [],
      detailHistoryLoading: false,
      detailPage: 1,
      detailPageSize: PAGE_SIZE,
      detailTotal: 0,
      renewRows: [],
      renewMode: 'single',
      renewType: '1',
      renewOptionCounts: {},
      renewOptionsLoading: false,
      renewVisible: false,
      renewLoading: false,
      processingVisible: false,
      batchProgress: null
    };
  }

  componentDidMount() {
    if (this.checkAccess()) {
      this.loadList();
    }
  }

  componentWillUnmount() {
    this.renewValidityRequestId = (this.renewValidityRequestId || 0) + 1;
    this.detailRequestId = (this.detailRequestId || 0) + 1;
    if (this.merchantSearchTimer) clearTimeout(this.merchantSearchTimer);
    if (this.storeSearchTimer) clearTimeout(this.storeSearchTimer);
  }

  checkAccess = () => {
    const identityValue = window.localStorage.getItem('identity');
    const identity = identityValue === null ? null : Number(identityValue);
    const allowedIdentity = this.props.moduleType === 'cms' ? 0 : 1;

    if (!window.localStorage.getItem('headParams') || (identity !== 0 && identity !== 1)) {
      this.setState({redirect: '/login'});
      return false;
    }
    if (identity !== allowedIdentity) {
      this.setState({redirect: identity === 0 ? '/cms' : '/ms'});
      return false;
    }
    return true;
  };

  getParams = () => {
    const {filters, page, pageSize} = this.state;
    const isCms = this.props.moduleType === 'cms';
    const agentNo = isCms ? filters.agentKeyword : getLoginValue('agentNo');
    const agentName = isCms ? filters.agentKeyword : getLoginValue('agentName');
    const merchant = splitCodeAndName(filters.merchantKeyword);
    const store = splitCodeAndName(filters.storeKeyword);
    const params = {
      agent_no: agentNo,
      agent_name: agentName,
      bind_core_merchant_code: merchant.code,
      bind_core_merchant_name: merchant.name,
      bind_merchant_code: store.code,
      bind_merchant_name: store.name,
      device_sn: filters.activationCode,
      expire_status: getExpiryStatusParam(filters.expiryStatus),
      activate_start_time: formatDate(filters.activationStart),
      activate_end_time: formatDate(filters.activationEnd),
      expire_start_time: formatDate(filters.expiryStart),
      expire_end_time: formatDate(filters.expiryEnd),
      page_size: pageSize,
      current_page: page
    };

    if (isCms) {
      params.platformCode = getLoginValue('platformCode');
    }

    return cleanRequestParams(params);
  };

  loadMerchantOptions = (type, searchContent = '') => {
    const loadingKey = type === 'merchant' ? 'merchantOptionsLoading' : 'storeOptionsLoading';
    const optionsKey = type === 'merchant' ? 'merchantOptions' : 'storeOptions';
    const searchFlag = type === 'merchant' ? 2 : 3;
    if (typeof window.api !== 'function') {
      this.setState({[loadingKey]: false});
      return;
    }

    const requestIdKey = `${type}OptionsRequestId`;
    this[`${type}LastRequestedSearch`] = searchContent;
    this[requestIdKey] = (this[requestIdKey] || 0) + 1;
    const requestId = this[requestIdKey];
    this.setState({[loadingKey]: true});
    window.api(MERCHANT_OPTIONS_SERVICE, cleanRequestParams({searchContent, searchFlag})).then(response => {
      if (this[requestIdKey] !== requestId) return;
      this.setState({[optionsKey]: normalizeMerchantOptions(response, type), [loadingKey]: false});
    }).catch(error => {
      if (this[requestIdKey] !== requestId) return;
      if (error && error.service_error_code === 'EPS000000801') {
        this.setState({redirect: '/login', [loadingKey]: false});
        return;
      }
      this.setState({[optionsKey]: [], [loadingKey]: false});
    });
  };

  updateMerchantSearch = (type, searchContent) => {
    const searchKey = type === 'merchant' ? 'merchantSearchContent' : 'storeSearchContent';
    const optionsKey = type === 'merchant' ? 'merchantOptions' : 'storeOptions';
    const loadingKey = type === 'merchant' ? 'merchantOptionsLoading' : 'storeOptionsLoading';
    const timerKey = `${type}SearchTimer`;
    const normalizedSearch = searchContent || '';
    if (this[`${type}LastRequestedSearch`] === normalizedSearch) return;
    if (this[timerKey]) clearTimeout(this[timerKey]);
    this.setState({[searchKey]: normalizedSearch, [optionsKey]: [], [loadingKey]: true});
    this[timerKey] = setTimeout(() => {
      this[timerKey] = null;
      this.loadMerchantOptions(type, normalizedSearch);
    }, MERCHANT_SEARCH_DEBOUNCE);
  };

  confirmMerchantSearch = (type) => {
    const searchKey = type === 'merchant' ? 'merchantSearchContent' : 'storeSearchContent';
    const searchContent = this.state[searchKey] || '';
    if (this[`${type}LastRequestedSearch`] === searchContent) return;
    const timerKey = `${type}SearchTimer`;
    if (this[timerKey]) clearTimeout(this[timerKey]);
    this[timerKey] = null;
    this.loadMerchantOptions(type, searchContent);
  };

  refreshMerchantOptions = (type, visible) => {
    const searchKey = type === 'merchant' ? 'merchantSearchContent' : 'storeSearchContent';
    const optionsKey = type === 'merchant' ? 'merchantOptions' : 'storeOptions';
    const loadingKey = type === 'merchant' ? 'merchantOptionsLoading' : 'storeOptionsLoading';
    const timerKey = `${type}SearchTimer`;
    if (this[timerKey]) clearTimeout(this[timerKey]);
    this[timerKey] = null;
    if (!visible) {
      this.setState({[optionsKey]: [], [loadingKey]: false});
      return;
    }
    this.setState({[searchKey]: '', [optionsKey]: [], [loadingKey]: true}, () => {
      this.loadMerchantOptions(type, '');
    });
  };

  renderMerchantSelect = (type, value) => {
    const isMerchant = type === 'merchant';
    const options = isMerchant ? this.state.merchantOptions : this.state.storeOptions;
    const loading = isMerchant ? this.state.merchantOptionsLoading : this.state.storeOptionsLoading;
    const visibleOptions = loading ? [] : options;
    return (
      <Select
        allowClear
        showSearch
        showArrow
        value={value || undefined}
        placeholder={isMerchant ? '商户编码/名称' : '门店编码/名称'}
        filterOption={false}
        loading={loading}
        notFoundContent={loading ? '加载中...' : '暂无数据'}
        onDropdownVisibleChange={visible => this.refreshMerchantOptions(type, visible)}
        onSearch={searchContent => this.updateMerchantSearch(type, searchContent)}
        onInputKeyDown={event => {
          if (event.key === 'Enter') this.confirmMerchantSearch(type);
        }}
        onBlur={() => this.confirmMerchantSearch(type)}
        onChange={selectedValue => this.changeFilter(isMerchant ? 'merchantKeyword' : 'storeKeyword', selectedValue || '')}
      >
        {visibleOptions.map(option => <Option key={option.value} value={option.value}>{option.label}</Option>)}
      </Select>
    );
  };

  requestList = () => {
    if (typeof window.api !== 'function') {
      const error = new Error('列表接口未初始化');
      error.service_error_message = '列表接口未初始化';
      return Promise.reject(error);
    }
    return window.api(RENEWAL_LIST_SERVICE, this.getParams());
  };

  loadList = () => {
    this.setState({loading: true});
    this.requestList().then(response => {
      const result = normalizeResponse(response);
      this.setState({list: result.list, total: result.total, loading: false});
    }).catch(error => {
      if (error && error.service_error_code === 'EPS000000801') {
        this.setState({redirect: '/login', loading: false});
        return;
      }
      this.setState({loading: false});
    });
  };

  changeFilter = (name, value) => {
    this.setState({
      filters: {...this.state.filters, [name]: value}
    });
  };

  handleSearch = () => {
    this.setState({page: 1}, this.loadList);
  };

  handleClear = () => {
    this.setState({filters: {...FILTER_INITIAL}, page: 1, selectedRowKeys: []}, this.loadList);
  };

  handleDateChange = (name, date) => {
    this.changeFilter(name, date);
  };

  handleRangeChange = (range) => {
    const rangeConfig = EXPIRY_RANGES.find(item => item.key === range);
    if (!rangeConfig || range === 'all') {
      this.setState({
        filters: {
          ...this.state.filters, expiryRange: 'all', expiryStart: null, expiryEnd: null
        }
      });
      return;
    }
    this.setState({
      filters: {
        ...this.state.filters,
        expiryRange: range,
        expiryStart: moment(),
        expiryEnd: moment().add(rangeConfig.days, 'days')
      }
    });
  };

  handlePageChange = (page, pageSize) => {
    this.setState({page, pageSize}, this.loadList);
  };

  showDetail = (record) => {
    this.detailRequestId = (this.detailRequestId || 0) + 1;
    const requestId = this.detailRequestId;
    this.setState({
      detail: record,
      detailHistory: [],
      detailHistoryLoading: true,
      detailPage: 1,
      detailPageSize: PAGE_SIZE,
      detailTotal: 0
    }, () => this.loadActivationHistory(record, 1, PAGE_SIZE, requestId));
  };

  closeDetail = () => {
    this.detailRequestId = (this.detailRequestId || 0) + 1;
    this.setState({
      detail: null,
      detailHistory: [],
      detailHistoryLoading: false,
      detailPage: 1,
      detailPageSize: PAGE_SIZE,
      detailTotal: 0
    });
  };

  getActivationHistoryParams = (record, page, pageSize) => cleanRequestParams({
    merchant_code: record.bind_core_merchant_code,
    store_code: record.bind_merchant_code,
    page_size: String(pageSize),
    current_page: String(page)
  });

  loadActivationHistory = (record, page, pageSize, requestId = this.detailRequestId) => {
    if (typeof window.api !== 'function') {
      this.handleActivationHistoryFailure('激活历史接口未初始化', requestId);
      return;
    }
    window.api(
      ACTIVATION_HISTORY_SERVICE,
      this.getActivationHistoryParams(record, page, pageSize)
    ).then(response => {
      if (this.detailRequestId !== requestId) return;
      const result = normalizeActivationHistory(response, page, pageSize);
      this.setState({
        detailHistory: result.list,
        detailHistoryLoading: false,
        detailPage: page,
        detailPageSize: pageSize,
        detailTotal: result.total
      });
    }).catch(error => {
      if (this.detailRequestId !== requestId) return;
      if (error && error.service_error_code === 'EPS000000801') {
        this.setState({redirect: '/login', detail: null, detailHistoryLoading: false});
        return;
      }
      this.handleActivationHistoryFailure(
        error && (error.service_error_message || error.message) || '激活历史加载失败',
        requestId
      );
    });
  };

  handleActivationHistoryFailure = (errorMessage, requestId) => {
    if (this.detailRequestId !== requestId) return;
    this.setState({detailHistory: [], detailHistoryLoading: false, detailTotal: 0}, () => {
      message.error(errorMessage);
    });
  };

  handleDetailPageChange = (page, pageSize) => {
    const {detail, detailPageSize} = this.state;
    if (!detail) return;
    const nextPageSize = pageSize || detailPageSize;
    const nextPage = nextPageSize === detailPageSize ? page : 1;
    this.detailRequestId = (this.detailRequestId || 0) + 1;
    const requestId = this.detailRequestId;
    this.setState({detailHistoryLoading: true}, () => {
      this.loadActivationHistory(detail, nextPage, nextPageSize, requestId);
    });
  };

  showRenew = (rows, mode = 'single') => {
    if (!rows || rows.length === 0) {
      message.warning('请先选择需要续费的记录');
      return;
    }
    Modal.confirm({
      title: '确认续费吗？',
      okText: '确定',
      cancelText: '取消',
      onOk: () => this.openRenewModal(rows, mode)
    });
  };

  startBatchFromTable = (rows) => {
    if (!rows || rows.length === 0) {
      message.warning('请先选择需要续费的记录');
      return;
    }
    Modal.confirm({
      title: '确认批量续费吗？',
      okText: '确定',
      cancelText: '取消',
      onOk: () => this.openRenewModal(rows, 'batch')
    });
  };

  getRenewOptionCount = (option, counts = this.state.renewOptionCounts) => Number(counts[option.period]) || 0;

  isRenewOptionAvailable = (option, rows) => this.getRenewOptionCount(option) >= rows.length;

  openRenewModal = (rows, mode) => {
    this.renewValidityRequestId = (this.renewValidityRequestId || 0) + 1;
    const requestId = this.renewValidityRequestId;
    this.setState({
      renewRows: rows,
      renewMode: mode,
      renewType: '',
      renewOptionCounts: {},
      renewOptionsLoading: true,
      renewVisible: true
    }, () => this.loadRenewalValidity(rows, requestId));
  };

  getRenewalOperator = () => {
    try {
      const headParams = JSON.parse(window.localStorage.getItem('headParams') || '{}');
      return headParams.login_name || '';
    } catch (error) {
      return '';
    }
  };

  getRenewalVersions = (rows) => {
    const versions = rows.map(row => row.merchant_version).filter(Boolean);
    return versions.filter((version, index) => versions.indexOf(version) === index);
  };

  getRenewalValidityParams = (version) => cleanRequestParams({
    out_trade_no: `EPS${window.common && typeof window.common.getRequestNo === 'function' ? window.common.getRequestNo(29) : Date.now()}`,
    agent_no: getLoginValue('agentNo'),
    merchant_version: version || 'WAICJ',
    operator_id: this.getRenewalOperator(),
    request_source: this.props.moduleType === 'cms' ? 'CMS' : 'MS'
  });

  getDeviceBindParams = (row) => cleanRequestParams({
    out_trade_no: `EPS${window.common && typeof window.common.getRequestNo === 'function' ? window.common.getRequestNo(29) : `${Date.now()}${Math.floor(Math.random() * 1000)}`}`,
    merchant_no: row.bind_merchant_code,
    core_merchant_code: row.bind_core_merchant_code,
    identify_type: 'VERSION',
    biz_tid: row.third_biz_tid,
    operator_id: this.getRenewalOperator(),
    merchant_version: row.merchant_version || 'WAICJ',
    bind_type: 2
  });

  requestDeviceBind = (row) => {
    if (typeof window.api !== 'function') {
      const error = new Error('续费接口未初始化');
      error.service_error_message = '续费接口未初始化';
      return Promise.reject(error);
    }
    return window.api(DEVICE_BIND_SERVICE, this.getDeviceBindParams(row));
  };

  loadRenewalValidity = (rows, requestId = this.renewValidityRequestId) => {
    const versions = this.getRenewalVersions(rows);
    if (versions.length === 0 || typeof window.api !== 'function') {
      this.handleRenewalValidityFailure('续费周期查询失败，请重试', requestId);
      return;
    }
    Promise.all(versions.map(version => window.api(
      RENEWAL_VALIDITY_SERVICE,
      this.getRenewalValidityParams(version)
    ))).then(responses => {
      if (this.renewValidityRequestId !== requestId) return;
      const counts = responses.reduce((result, response) => {
        const responseCounts = getRenewalValidityCounts(response);
        Object.keys(responseCounts).forEach(period => {
          result[period] = (result[period] || 0) + responseCounts[period];
        });
        return result;
      }, {});
      const firstAvailableOption = RENEW_OPTIONS.find(option => (
        this.getRenewOptionCount(option, counts) >= rows.length
      ));
      if (!firstAvailableOption) {
        this.setState({
          renewOptionCounts: counts, renewOptionsLoading: false, renewVisible: false, renewRows: []
        }, () => {
          message.warning('当前选择的激活码数量不足，请联系工作人员');
        });
        return;
      }
      this.setState({
        renewOptionCounts: counts, renewType: firstAvailableOption.key, renewOptionsLoading: false
      });
    }).catch(error => {
      if (this.renewValidityRequestId !== requestId) return;
      if (error && error.service_error_code === 'EPS000000801') {
        this.setState({redirect: '/login', renewOptionsLoading: false, renewVisible: false});
        return;
      }
      this.handleRenewalValidityFailure(error && (error.service_error_message || error.message) || '续费周期查询失败，请重试', requestId);
    });
  };

  handleRenewalValidityFailure = (errorMessage, requestId = this.renewValidityRequestId) => {
    if (this.renewValidityRequestId !== requestId) return;
    this.setState({renewOptionsLoading: false, renewOptionCounts: {}}, () => message.error(errorMessage));
  };

  closeRenew = () => {
    if (!this.state.renewLoading) {
      this.renewValidityRequestId = (this.renewValidityRequestId || 0) + 1;
      this.setState({
        renewVisible: false,
        renewRows: [],
        renewMode: 'single',
        renewType: '',
        renewOptionCounts: {},
        renewOptionsLoading: false
      });
    }
  };

  confirmRenew = () => {
    const {
      renewRows, renewMode, renewType, renewOptionsLoading
    } = this.state;
    if (renewOptionsLoading || !renewType) return;
    const selectedOption = RENEW_OPTIONS.find(item => item.key === renewType);
    if (!selectedOption || !this.isRenewOptionAvailable(selectedOption, renewRows)) {
      message.warning('当前选择的激活码数量不足，请联系工作人员');
      return;
    }
    if (this.state.renewMode === 'batch') {
      this.startBatchRenew();
      return;
    }
    this.setState({renewLoading: true});
    Promise.resolve().then(() => this.requestDeviceBind(renewRows[0])).then(() => {
      message.success('续费申请已提交');
      this.setState({
        renewVisible: false, renewRows: [], selectedRowKeys: [], renewLoading: false, renewMode: 'single'
      });
      this.loadList();
    }).catch(error => {
      this.setState({renewLoading: false});
      if (error && error.service_error_code === 'EPS000000801') {
        this.setState({redirect: '/login'});
        return;
      }
      message.error(error && (error.service_error_message || error.message) || '续费失败，请重试');
    });
  };

  startBatchRenew = () => {
    const {renewRows} = this.state;
    const selectedOption = RENEW_OPTIONS.find(item => item.key === this.state.renewType);
    if (!selectedOption || !this.isRenewOptionAvailable(selectedOption, renewRows)) {
      message.warning('当前选择的激活码数量不足，请联系工作人员');
      return;
    }
    if (typeof window.api !== 'function') {
      message.error('续费接口未初始化，请联系工作人员');
      return;
    }
    const total = renewRows.length;
    this.setState({
      renewVisible: false,
      processingVisible: true,
      batchProgress: {
        total,
        processed: 0,
        success: 0,
        failed: 0,
        currentStore: '正在准备续费任务',
        failedRows: [],
        complete: false
      }
    }, () => this.processBatchChunk(0));
  };

  processBatchChunk = (startIndex) => {
    const {renewRows} = this.state;
    if (startIndex >= renewRows.length) {
      this.finishBatchRenew({});
      return;
    }
    const row = renewRows[startIndex];
    const endIndex = Math.min(startIndex + BATCH_RENEW_SIZE, renewRows.length);
    this.setState({
      batchProgress: {
        ...this.state.batchProgress,
        currentStore: `正在处理第${startIndex + 1}-${endIndex}家门店`
      }
    }, () => {
      Promise.resolve().then(() => this.requestDeviceBind(row)).then(() => {
        this.finishBatchChunk({successCount: 1}, [row], startIndex);
      }).catch(error => {
        this.finishBatchChunk({failedRows: [row], failedCount: 1, error}, [row], startIndex);
      });
    });
  };

  finishBatchChunk = (result = {}, chunk, startIndex) => {
    const previous = this.state.batchProgress;
    let failedRows = [];
    if (Array.isArray(result.failedRows)) {
      failedRows = result.failedRows;
    } else if (Array.isArray(result.failed)) {
      failedRows = result.failed;
    }
    const failedValue = Number(result.failedCount);
    const failed = Number.isNaN(failedValue)
      ? failedRows.length
      : Math.max(0, Math.min(chunk.length, failedValue));
    const successValue = Number(result.successCount);
    const success = Number.isNaN(successValue)
      ? Math.max(chunk.length - failed, 0)
      : Math.max(0, Math.min(chunk.length - failed, successValue));
    const processed = previous.processed + chunk.length;
    this.setState({
      batchProgress: {
        ...previous,
        processed,
        success: previous.success + success,
        failed: previous.failed + failed,
        failedRows: previous.failedRows.concat(failedRows),
        currentStore: `已处理${processed}家门店`
      },
      renewLoading: false
    }, () => {
      if (result.error) message.error(result.error.service_error_message || '本批次续费失败，请重试');
      this.processBatchChunk(startIndex + chunk.length);
    });
  };

  finishBatchRenew = () => {
    const {batchProgress, renewRows} = this.state;
    this.setState({
      batchProgress: {
        ...batchProgress,
        processed: renewRows.length,
        currentStore: '全部门店处理完成',
        complete: true
      },
      renewLoading: false
    });
  };

  closeProcessing = () => {
    const {batchProgress} = this.state;
    if (batchProgress && !batchProgress.complete) {
      message.warning('续费正在处理中，请稍候');
      return;
    }
    this.setState({
      processingVisible: false, batchProgress: null, renewRows: [], renewMode: 'single', selectedRowKeys: []
    });
    this.loadList();
  };

  getColumns = () => {
    const {moduleType} = this.props;
    const isCms = moduleType === 'cms';
    const columns = [
      {
        title: '序号',
        key: 'index',
        width: 68,
        fixed: 'left',
        align: 'center',
        render: (text, record, index) => (this.state.page - 1) * this.state.pageSize + index + 1
      },
      {
        title: isCms ? '代理商编码/名称' : '商户编码/名称',
        dataIndex: isCms ? 'agent' : 'merchant',
        key: isCms ? 'agent' : 'merchant',
        width: 300,
        render: text => <span className="cell-wrap">{text}</span>
      },
      {
        title: isCms ? '商户编码/名称' : '门店编码/门店名称',
        dataIndex: isCms ? 'merchant' : 'store',
        key: isCms ? 'merchant' : 'store',
        width: 300,
        render: text => <span className="cell-wrap">{text}</span>
      },
      {
        title: isCms ? '门店编码/门店名称' : '激活版本',
        dataIndex: isCms ? 'store' : 'version',
        key: isCms ? 'store' : 'version',
        width: isCms ? 300 : 170,
        render: text => <span className="cell-wrap">{text}</span>
      }
    ];

    columns.push(
      ...(isCms ? [{
        title: '激活版本',
        dataIndex: 'version',
        key: 'version',
        width: 120,
        render: text => <span className="cell-wrap">{text}</span>
      }] : []),
      {
        title: '激活码',
        dataIndex: 'code',
        key: 'code',
        width: 250,
        render: text => <span className="cell-wrap">{text}</span>
      },
      {
        title: '激活状态',
        dataIndex: 'activationStatus',
        key: 'activationStatus',
        width: 100,
        align: 'center',
        render: text => <span className={getStatusClass(text)}>{text}</span>
      },
      {
        title: '到期状态',
        dataIndex: 'expiryStatus',
        key: 'expiryStatus',
        width: 100,
        align: 'center',
        render: text => <span className={getStatusClass(text)}>{text}</span>
      },
      {
        title: '有效期',
        dataIndex: 'validity',
        key: 'validity',
        width: 120,
        align: 'center',
        render: text => <span className="validity-text">{text}</span>
      },
      {
        title: '激活时间',
        dataIndex: 'activationTime',
        key: 'activationTime',
        width: 200,
        align: 'center'
      },
      {
        title: '到期时间',
        dataIndex: 'expiryTime',
        key: 'expiryTime',
        width: 200,
        align: 'center'
      },
      {
        title: '操作',
        key: 'action',
        width: isCms ? 90 : 145,
        fixed: 'right',
        align: 'center',
        render: (text, record) => (
          <span className="table-actions">
            {!isCms && <a onClick={() => this.showRenew([record])}>续费</a>}
            <a onClick={() => this.showDetail(record)}>查看</a>
          </span>
        )
      }
    );
    return columns;
  };

  renderFilter = (label, content, className = '') => (
    <div className={`filter-item ${className}`}>
      <span className="filter-label">{label}</span>
      {content}
    </div>
  );

  renderDateRange = (startName, endName, startValue, endValue) => (
    <div className="date-range">
      <DatePicker
        format={DATE_FORMAT}
        placeholder="请选择起始时间"
        value={startValue}
        onChange={date => this.handleDateChange(startName, date)}
      />
      <span className="date-range__separator">-</span>
      <DatePicker
        format={DATE_FORMAT}
        placeholder="请选择结束时间"
        value={endValue}
        onChange={date => this.handleDateChange(endName, date)}
      />
    </div>
  );

  renderFilters = () => {
    const {filters, total} = this.state;
    const isMs = this.props.moduleType === 'ms';
    return (
      <section className="filter-panel">
        <div className="filter-grid">
          {!isMs && this.renderFilter('代理商编码/名称:', <Input value={filters.agentKeyword} placeholder="代理商代码/名称" onChange={e => this.changeFilter('agentKeyword', e.target.value)} />)}
          {this.renderFilter('商户编码/名称:', this.renderMerchantSelect('merchant', filters.merchantKeyword))}
          {this.renderFilter('门店编码/名称:', this.renderMerchantSelect('store', filters.storeKeyword))}
          {this.renderFilter('激活码:', <Input value={filters.activationCode} placeholder="激活码" onChange={e => this.changeFilter('activationCode', e.target.value)} />)}
          {this.renderFilter('激活时间:', this.renderDateRange('activationStart', 'activationEnd', filters.activationStart, filters.activationEnd), 'filter-item--wide')}
          {this.renderFilter('到期时间:', this.renderDateRange('expiryStart', 'expiryEnd', filters.expiryStart, filters.expiryEnd), 'filter-item--wide')}
          {this.renderFilter(
            '到期状态:', (
              <Select value={filters.expiryStatus || undefined} placeholder="全部" onChange={value => this.changeFilter('expiryStatus', value)} allowClear>
                <Option value="">全部</Option>
                <Option value="2">未过期</Option>
                <Option value="1">已过期</Option>
              </Select>
            )
          )}
        </div>
        <div className="quick-filter-row">
          <span className="filter-label">到期快捷筛选:</span>
          <div className="quick-filter-list">
            {EXPIRY_RANGES.map(item => (
              <button
                className={`quick-filter ${filters.expiryRange === item.key ? 'quick-filter--active' : ''}`}
                key={item.key}
                type="button"
                onClick={() => this.handleRangeChange(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
        <div className="filter-actions">
          <div className="filter-actions__buttons">
            <Button type="primary" icon="search" onClick={this.handleSearch}>查询</Button>
            <Button icon="reload" onClick={this.handleClear}>清空</Button>
          </div>
          <span className="filter-result-count">共 {Number(total || 0).toLocaleString('en-US')} 条记录</span>
        </div>
      </section>
    );
  };

  renderDetail = () => {
    const {
      detail, detailHistory, detailHistoryLoading, detailPage, detailPageSize, detailTotal
    } = this.state;
    if (!detail) return null;
    return (
      <Drawer
        title="查看门店激活历史"
        placement="right"
        width={700}
        visible
        onClose={this.closeDetail}
        className="activation-history-drawer"
      >
        <div className="history-meta">
          <span>{detail.store}</span>
          <span>{detail.version}</span>
        </div>
        <Table
          bordered
          rowKey="historyKey"
          size="middle"
          loading={detailHistoryLoading}
          pagination={{
            current: detailPage,
            pageSize: detailPageSize,
            total: detailTotal,
            showQuickJumper: true,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: count => `共 ${count} 条`,
            onChange: this.handleDetailPageChange,
            onShowSizeChange: this.handleDetailPageChange
          }}
          dataSource={detailHistory}
          locale={{emptyText: <Empty description="暂无激活历史" />}}
          columns={[
            {
              title: '序号', dataIndex: 'index', key: 'index', width: 60, align: 'center'
            },
            {
              title: '激活码', dataIndex: 'activationCode', key: 'activationCode', render: text => <span className="cell-wrap">{text}</span>
            },
            {
              title: '激活时间', dataIndex: 'activationTime', key: 'activationTime', width: 160
            },
            {
              title: '到期时间', dataIndex: 'expiryTime', key: 'expiryTime', width: 160
            }
          ]}
        />
      </Drawer>
    );
  };

  renderRenewModal = () => {
    const {
      renewVisible, renewRows, renewType, renewMode, renewLoading, renewOptionCounts, renewOptionsLoading
    } = this.state;
    const selectedOption = RENEW_OPTIONS.find(item => item.key === renewType) || RENEW_OPTIONS[0];
    return (
      <Modal
        title="请选择续费时间"
        visible={renewVisible}
        confirmLoading={renewLoading || renewOptionsLoading}
        onOk={this.confirmRenew}
        onCancel={this.closeRenew}
        okText="确定"
        cancelText="取消"
        width={560}
        className="renew-type-modal"
      >
        {renewOptionsLoading ? <div className="renew-options-loading"><Spin tip="正在查询可用续费周期..." /></div> : (
          <Radio.Group value={renewType} onChange={event => this.setState({renewType: event.target.value})}>
            {RENEW_OPTIONS.map(option => (
              <Radio
                value={option.key}
                key={option.key}
                disabled={!this.isRenewOptionAvailable(option, renewRows)}
              >
                <span className="renew-option-label">有效期：<strong>{option.label}</strong>/{option.days}</span>
                <span className="renew-option-count">（{this.getRenewOptionCount(option, renewOptionCounts)}个）</span>
              </Radio>
            ))}
          </Radio.Group>
        )}
        {!renewOptionsLoading && renewType && <p className="renew-type-hint">本次将为 {renewRows.length} 家门店续费 {selectedOption.label}。</p>}
      </Modal>
    );
  };

  renderProcessingModal = () => {
    const {processingVisible, batchProgress, renewRows} = this.state;
    if (!batchProgress) return null;
    const percent = batchProgress.total ? Math.round(batchProgress.processed / batchProgress.total * 100) : 0;
    return (
      <Modal
        title={<div><span className="processing-eyebrow">批量操作 / 插件续费</span><strong>{batchProgress.complete ? '批量续费处理完成' : '正在处理续费'}</strong></div>}
        width={620}
        centered
        visible={processingVisible}
        closable={batchProgress.complete}
        keyboard={batchProgress.complete}
        maskClosable={false}
        onCancel={this.closeProcessing}
        className="renew-processing-modal"
        wrapClassName="renew-processing-modal-wrap"
        footer={(
          <div className="processing-footer">
            <Button onClick={this.closeProcessing} disabled={!batchProgress.complete}>{batchProgress.complete ? '关闭' : '处理中...'}</Button>
          </div>
        )}
      >
        <div className="processing-summary">
          <div className="processing-summary__headline">
            <div><strong>{percent}%</strong><span>{batchProgress.complete ? '全部门店处理完成' : batchProgress.currentStore}</span></div>
            <span className={`processing-badge ${batchProgress.complete ? 'processing-badge--success' : ''}`}><i />{batchProgress.complete ? '处理完成' : '处理中'}</span>
          </div>
          <div className="processing-progress"><span style={{width: `${percent}%`}} /></div>
          <div className="processing-progress-meta"><span>处理进度</span><strong>{batchProgress.processed} / {batchProgress.total} 家门店</strong></div>
        </div>
        <div className="processing-stat-grid">
          <div><span className="stat-icon stat-icon--total"><Icon type="database" /></span><span>待处理总数<strong>{batchProgress.total}</strong></span></div>
          <div><span className="stat-icon stat-icon--processing"><Icon type="sync" /></span><span>已处理<strong>{batchProgress.processed}</strong></span></div>
          <div><span className="stat-icon stat-icon--success"><Icon type="check-circle" /></span><span>续费成功<strong>{batchProgress.success}</strong></span></div>
          <div><span className="stat-icon stat-icon--failed"><Icon type="exclamation-circle" /></span><span>续费失败<strong>{batchProgress.failed}</strong></span></div>
        </div>
        {batchProgress.failed > 0 && (
          <div className="failed-store-panel">
            <div><strong>失败门店编码/门店名称</strong><b>{batchProgress.failed} 个</b></div>
            <p>以下门店未完成续费，请核对后重试</p>
            {batchProgress.failedRows.length > 0 ? (
              <ul>{batchProgress.failedRows.map((row, index) => {
                const storeLabel = combineCodeAndName(row.bind_merchant_code, row.bind_merchant_name) || row.store;
                return <li key={index}>{storeLabel}</li>;
              })}</ul>
            ) : <span className="failed-store-empty">暂无失败门店</span>}
          </div>
        )}
        <p className="processing-tip">处理期间请保持页面打开，完成后可关闭此窗口。</p>
        {!batchProgress.complete && renewRows.length === 0 && <Spin />}
      </Modal>
    );
  };

  render() {
    const {moduleType} = this.props;
    const {
      redirect, filters, list, total, page, pageSize, selectedRowKeys, loading, renewVisible, renewRows
    } = this.state;
    if (redirect) return <Redirect to={redirect} />;
    const isCms = moduleType === 'cms';
    const selectedRows = list.filter(item => selectedRowKeys.indexOf(item.id) >= 0);
    const rowSelection = {
      selectedRowKeys,
      onChange: keys => this.setState({selectedRowKeys: keys})
    };
    const pagination = {
      current: page,
      pageSize,
      total,
      showQuickJumper: true,
      showSizeChanger: true,
      pageSizeOptions: ['10', '20', '50', '100'],
      showTotal: count => `共 ${count} 条`,
      onChange: this.handlePageChange,
      onShowSizeChange: this.handlePageChange
    };

    return (
      <div className="activation-page">
        <header className="module-header">
          <div>
            {/* <span className="module-header__eyebrow">{isCms ? 'CMS' : 'MS'} 权限模块</span> */}
            <h1>插件续费</h1>
          </div>
        </header>
        {this.renderFilters()}
        <section className="table-panel">
          <div className="table-panel__header">
            <div className="table-panel__title">
              <h2>插件列表</h2>
              {!isCms && <span className={selectedRows.length > 0 ? 'light' : 'normal'}>已选{selectedRows.length}条</span>}
            </div>
            {!isCms && <Button className="table-panel__batch-button" icon="reload" disabled={selectedRows.length === 0} onClick={() => this.startBatchFromTable(selectedRows)}>批量续费</Button>}
          </div>
          <Spin spinning={loading}>
            <Table
              bordered
              rowKey="id"
              size="middle"
              rowSelection={isCms ? undefined : rowSelection}
              columns={this.getColumns()}
              dataSource={list}
              pagination={pagination}
              scroll={{x: isCms ? 1640 : 1500}}
              locale={{emptyText: <Empty description="暂无数据" />}}
            />
          </Spin>
        </section>
        {this.renderDetail()}
        {this.renderRenewModal()}
        {this.renderProcessingModal()}
      </div>
    );
  }
}

export default ActivationList;



// WEBPACK FOOTER //
// ./src/containers/module/activationManagement/activationList.jsx